import type { Mob, MobType } from "@mmotdg/shared";
import * as THREE from "three";
import { createMobBodyGeometry, createMobBodyMaterial } from "./models/mobs/core.js";

interface MobView {
  group: THREE.Group;
  body: THREE.Mesh;
  hpBar: THREE.Mesh;
  hpBarBg: THREE.Mesh;
  hpBarMat: THREE.MeshBasicMaterial;
  bodyMat: THREE.MeshStandardMaterial;
  baseEmissive: THREE.Color;
  baseEmissiveIntensity: number;
  maxHp: number;
  hitFlashUntil: number;
  spawnAt: number;
}

const HIT_FLASH_MS = 110;
const SPAWN_PULSE_MS = 320;

export class MobRenderer {
  private readonly group = new THREE.Group();
  private readonly views = new Map<string, MobView>();
  private readonly bodyGeometries = new Map<MobType, THREE.BufferGeometry>();
  private readonly bodyMaterials = new Map<MobType, THREE.MeshStandardMaterial>();
  private readonly hpBarBgGeometry = new THREE.PlaneGeometry(0.8, 0.08);
  private readonly hpBarFgGeometry = new THREE.PlaneGeometry(0.78, 0.06);
  private readonly hpBarBgMaterial = new THREE.MeshBasicMaterial({
    color: 0x000000,
    transparent: true,
    opacity: 0.6,
  });

  private transform: import("./LaneTransform.js").LaneTransform | null = null;

  constructor(private readonly target: THREE.Object3D) {
    this.target.add(this.group);
  }

  setTransform(t: import("./LaneTransform.js").LaneTransform | null): void {
    this.transform = t;
  }

  private toWorld(x: number, y: number): { x: number; z: number } {
    if (this.transform) return this.transform.flatToWorld(x, y);
    return { x, z: y };
  }

  sync(mobs: Mob[]): void {
    const seen = new Set<string>();
    for (const mob of mobs) {
      seen.add(mob.id);
      let view = this.views.get(mob.id);
      if (!view) view = this.create(mob);
      this.updateHpBar(view, mob);
    }
    for (const [id, view] of this.views) {
      if (!seen.has(id)) {
        this.group.remove(view.group);
        view.hpBarMat.dispose();
        view.bodyMat.dispose();
        this.views.delete(id);
      }
    }
  }

  flashDamage(id: string): void {
    const view = this.views.get(id);
    if (!view) return;
    view.hitFlashUntil = performance.now() + HIT_FLASH_MS;
  }

  private getBodyGeometry(type: MobType): THREE.BufferGeometry {
    let geo = this.bodyGeometries.get(type);
    if (!geo) {
      geo = createMobBodyGeometry(type);
      this.bodyGeometries.set(type, geo);
    }
    return geo;
  }

  private getSharedBodyMaterial(type: MobType): THREE.MeshStandardMaterial {
    let mat = this.bodyMaterials.get(type);
    if (!mat) {
      mat = createMobBodyMaterial(type);
      this.bodyMaterials.set(type, mat);
    }
    return mat;
  }

  private create(mob: Mob): MobView {
    const g = new THREE.Group();
    const sharedMat = this.getSharedBodyMaterial(mob.type);
    const bodyMat = sharedMat.clone();
    const body = new THREE.Mesh(this.getBodyGeometry(mob.type), bodyMat);
    body.castShadow = true;
    body.position.y = mob.stats.isFlying ? 1.2 : 0.3;
    g.add(body);

    const hpBg = new THREE.Mesh(this.hpBarBgGeometry, this.hpBarBgMaterial);
    hpBg.position.set(0, mob.stats.isFlying ? 1.8 : 0.95, 0);
    g.add(hpBg);

    const hpBarMat = new THREE.MeshBasicMaterial({ color: 0x5dd39e });
    const hp = new THREE.Mesh(this.hpBarFgGeometry, hpBarMat);
    hp.position.set(0, mob.stats.isFlying ? 1.81 : 0.96, 0.001);
    g.add(hp);

    {
      const pw = this.toWorld(mob.position.x, mob.position.y);
      g.position.set(pw.x, 0, pw.z);
    }
    this.group.add(g);
    const view: MobView = {
      group: g,
      body,
      hpBar: hp,
      hpBarBg: hpBg,
      hpBarMat,
      bodyMat,
      baseEmissive: bodyMat.emissive.clone(),
      baseEmissiveIntensity: bodyMat.emissiveIntensity,
      maxHp: mob.stats.maxHp,
      hitFlashUntil: 0,
      spawnAt: performance.now(),
    };
    this.views.set(mob.id, view);
    return view;
  }

  private updateHpBar(view: MobView, mob: Mob): void {
    const ratio = Math.max(0, mob.hp / view.maxHp);
    view.hpBar.scale.x = ratio;
    view.hpBar.position.x = -0.39 * (1 - ratio);
    const color = ratio > 0.5 ? 0x5dd39e : ratio > 0.25 ? 0xffb454 : 0xff5e6b;
    view.hpBarMat.color.setHex(color);
  }

  render(
    mobs: Mob[],
    prevPositions: Map<string, { x: number; y: number }>,
    alpha: number,
    camera: THREE.Camera,
    _dt: number,
  ): void {
    const now = performance.now();
    for (const mob of mobs) {
      const view = this.views.get(mob.id);
      if (!view) continue;
      const prev = prevPositions.get(mob.id);
      const px = prev ? prev.x : mob.position.x;
      const py = prev ? prev.y : mob.position.y;
      const interpX = px + (mob.position.x - px) * alpha;
      const interpY = py + (mob.position.y - py) * alpha;
      const pw = this.toWorld(interpX, interpY);
      view.group.position.x = pw.x;
      view.group.position.z = pw.z;
      view.body.rotation.y += 0.02;
      view.hpBar.quaternion.copy(camera.quaternion);
      view.hpBarBg.quaternion.copy(camera.quaternion);

      const spawnK = Math.min(1, (now - view.spawnAt) / SPAWN_PULSE_MS);
      if (spawnK < 1) {
        const scale = 0.4 + spawnK * 0.6;
        view.body.scale.set(scale, scale, scale);
        view.bodyMat.emissiveIntensity = view.baseEmissiveIntensity + (1 - spawnK) * 0.8;
      } else if (view.body.scale.x !== 1) {
        view.body.scale.set(1, 1, 1);
      }

      if (view.hitFlashUntil > now) {
        const k = (view.hitFlashUntil - now) / HIT_FLASH_MS;
        view.bodyMat.emissive.setRGB(1, 1, 1);
        view.bodyMat.emissiveIntensity = view.baseEmissiveIntensity + k * 1.6;
      } else if (spawnK >= 1) {
        view.bodyMat.emissive.copy(view.baseEmissive);
        view.bodyMat.emissiveIntensity = view.baseEmissiveIntensity;
      }
    }
  }

  dispose(): void {
    for (const view of this.views.values()) {
      this.group.remove(view.group);
      view.hpBarMat.dispose();
      view.bodyMat.dispose();
    }
    this.views.clear();
    for (const geo of this.bodyGeometries.values()) geo.dispose();
    this.bodyGeometries.clear();
    for (const mat of this.bodyMaterials.values()) mat.dispose();
    this.bodyMaterials.clear();
    this.hpBarBgGeometry.dispose();
    this.hpBarFgGeometry.dispose();
    this.hpBarBgMaterial.dispose();
    this.target.remove(this.group);
  }
}
